import {ADOPTION_TARGET, WEEKS_TARGET} from "./consts";
import {fetchLocations} from "../factories/locations";

/**
 *
 * @param state
 * @returns {*}
 */
export const getAppState = ({simulation: state}) => state;

/**
 *
 * @param actionsCollection
 * @param add
 * @returns {*}
 */
export const totalWeeksRequired = (actionsCollection, add=0) =>
  actionsCollection.reduce((x, y) => x + y.weeks_required, add);

/**
 *
 * @param action
 * @param history
 * @returns {*}
 */
export const weeksRequiredTillAction = (action, history) => {
  const pos = history.findIndex(each => each.id === action.id);
  if (pos >= 0) {
    return history
      .filter((_, i) => i < pos)
      .reduce((x, y) => x + y.weeks_required, 1);
  }
};

/**
 *
 * @param action
 * @param history
 * @returns {*}
 */
export const weeksRequiredFromAction = (action, history) => {
  const pos = history.findIndex(each => each.id === action.id);
  if (pos >= 0) {
    return history
      .filter((_, i) => i >= pos)
      .reduce((x, y) => x + y.weeks_required, 0);
  }
};

/**
 *
 * @param action
 * @param history
 * @returns {*}
 */
export const weeksRequiredAfterAction = (action, history) => {
  const pos = history.findIndex(each => each.id === action.id);
  if (pos >= 0) {
    return history
      .filter((_, i) => i > pos)
      .reduce((x, y) => x + y.weeks_required, 0);
  }
};

/**
 *
 * @param html
 * @returns {string}
 */
export const stripTags = html => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || div.innerText || "";
  return text.replace(/[\r\n\x0B\x0C\u0085\u2028\u2029]+/g,"");
};

/**
 *
 * @param num
 * @param floor
 * @param ceil
 * @returns {*}
 */
export const limit = (num, floor, ceil) => num < floor ? floor : (num > ceil ? ceil : num);

/**
 *
 * @param locations
 * @param history
 * @returns {{completed: null, completedError: null, completedSuccess: null}}
 */
export function getCompletedState(locations, history) {
  const state = {
    completed: null,
    completedError: null,
    completedSuccess: null,
  };
  const weeksTotal = history.reduce((a, b) => a + b.weeks_required, 1);
  const totalAdoption = locations.reduce((a, b) => a + b.adoption, 0);
  const avgAdoption = totalAdoption / locations.length;
  const goalCondition = locations.every(each => each.adoption >= ADOPTION_TARGET);
  if (goalCondition && avgAdoption >= ADOPTION_TARGET && weeksTotal <= WEEKS_TARGET) {
    state.completed = true;
    state.completedSuccess = true;
  }
  if (!goalCondition && avgAdoption < ADOPTION_TARGET && weeksTotal >= WEEKS_TARGET) {
    state.completed = true;
    state.completedError = true;
  }
  return state;
}

/**
 *
 * @param arr
 * @returns {*}
 */
export function removeDuplicates(arr) {
  return arr.filter((each, pos) => arr.findIndex(item => item.id === each.id) === pos)
}

/**
 *
 * @type {{controller: null, scene: null}}
 */
export const scrollConfig = {
  controller: null,
  scene: null,
};

/**
 *
 */
export function resetScrollConfig() {
  scrollConfig.scene && scrollConfig.scene.remove();
  scrollConfig.scene && scrollConfig.scene.destroy(true);
  scrollConfig.scene = null;
  scrollConfig.controller && scrollConfig.controller.destroy(true);
  scrollConfig.controller = null;
}

/**
 *
 */
export function precacheImages() {
  const preloads = [
    "flow-utilization.svg",
    "img-community@2x.png",
    "img-community-a@2x.png",
    "img-community-c@2x.png",
    "img-EPI-focal-point@2x.png",
    "img-facility-b@2x.png",
    "img-facility-b-1@2x.png",
    "img-health-officer@2x.png",
    "img-manager@2x.png",
    "img-stakeholder-base.svg",
    "img-trophy.svg",
    "img-vaccinator@2x.png",
    "img-vaccinator-a@2x.png",
    "img-vaccinator-c@2x.png",
    "DHT.png",
    "facility-a.png",
    "facility-b.png",
    "facility-c.png",
  ].map(each => `/img/changemanagement/${each}`);
  let tempImg = [];

  for(let x=0; x<preloads.length; x++) {
    tempImg[x] = new Image();
    tempImg[x].src = preloads[x];
  }
}

/**
 *
 * @param stakeholder
 * @returns {boolean}
 */
export const testStakeholderIsStaff = stakeholder => (
  Object.keys(stakeholder).includes('motivation')
  && Object.keys(stakeholder).includes('ability')
);

/**
 *
 * @param a
 * @param b
 * @returns {string}
 */
export function similarityLikelihood(a,b) {
  let equivalency = 0;
  let minLength = (a.length > b.length) ? b.length : a.length;
  let maxLength = (a.length < b.length) ? b.length : a.length;
  for(let i = 0; i < minLength; i++) {
    if(a[i] == b[i]) {
      equivalency++;
    }
  }

  let weight = equivalency / maxLength;
  return weight;
}

/**
 * test input: [1, 2, 3, 4, 5]
 * outputs: [[1,2,3,4,5], [1,2,3,4], [1,2,3], [1,2], [1], []]
 * @param arr
 * @returns {*}
 */
export function arrayCascade(arr) {
  const cascade = arr.map((each, i, arr) => arr.filter((_, j) => j <= i)).reverse();
  cascade.push([]);
  return cascade;
}

/**
 *
 * @param history
 * @param location
 * @returns {{successfulMeetingIndex: *, allMeetings: Array, microplanningMeetingIndex: *}}
 */
export function getQITMeetingTimeline (history, location) {
  const allMeetings = [];
  let successfulMeetingIndex;
  let microplanningMeetingIndex;
  const e1Index = history.findIndex(each => each.id === 'E1');
  if (e1Index >= 0) {
    let lookupWeeks = e1Index > 0 ? totalWeeksRequired(history.slice(0, e1Index)) : 1;
    let microplanningLookupWeeks = lookupWeeks + 14;
    let i = e1Index, len = history.length;
    while (i < len) {
      const historySlice = history.slice(0, i +1);
      const timeline = historySlice.slice();
      const action = timeline.pop();
      if (totalWeeksRequired(historySlice, 1) >= lookupWeeks) {
        const testTimeline = [
          ...timeline,
          {
            ...action,
            weeks_required: action.weeks_required - (totalWeeksRequired(historySlice, 1) - lookupWeeks)
          }
        ];
        allMeetings.push(testTimeline);
        if (!successfulMeetingIndex) {
          const pureLocations = fetchLocations(testTimeline);
          const pureLocation = pureLocations.find(each => each.id === location.id);
          const successfulQITMeeting = pureLocation.stakeholders.every(each => {
            if (testStakeholderIsStaff(each)) {
              return each.ability >= 60 && each.motivation >= 60;
            } else {
              return each.participation >= 30;
            }
          });
          if (successfulQITMeeting) {
            successfulMeetingIndex = allMeetings.length -1;
          }
        }
        lookupWeeks += 6;
      }
      if (totalWeeksRequired(historySlice, 1) >= microplanningLookupWeeks && !microplanningMeetingIndex) {
        const testTimeline = [
          ...timeline,
          {
            ...action,
            weeks_required: action.weeks_required - (totalWeeksRequired(historySlice, 1) -microplanningLookupWeeks)
          }
        ];
        allMeetings.push(testTimeline);
        microplanningMeetingIndex = allMeetings.length -1;
      }
      i++;
    }
  }
  return {
    allMeetings,
    successfulMeetingIndex,
    microplanningMeetingIndex
  };
}
